import React from "react";
import {
  Check,
  CornerDownRight,
  FileText,
  Paperclip,
  PlusCircle,
  X
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";

import { useCitationDocument } from "../context/CitationDocumentsContext";
import { useCitationViewer } from "../context/CitationViewerContext";
import type { CaseyProposal, ProposalDecision } from "../types";
import { CitationSnippet } from "../../../components/common/citations/CitationSnippet";

type Props = {
  proposal: CaseyProposal;
  decision: ProposalDecision;
  onAccept: () => void;
  onReject: () => void;
};

/**
 * One suggested answer, with the evidence behind it and nothing written to the
 * form until the client says so.
 *
 * The citation is not decoration: a client who cannot see where a value came
 * from has to re-check the document themselves, which is the work the proposal
 * was supposed to save.
 */
export function ProposalCard({
  proposal,
  decision,
  onAccept,
  onReject
}: Props) {
  const applying = decision === "applying";
  const settled = decision === "accepted" || decision === "rejected";
  const isFileProposal = proposal.kind === "document" || Boolean(proposal.file);
  const viewer = useCitationViewer();

  const geometry = {
    polygon: proposal.polygon,
    pageWidth: proposal.page_width,
    pageHeight: proposal.page_height
  };
  // Every part has to be present before this is worth a download: a polygon with
  // no page size to scale against cannot be placed, so asking for the rendition
  // would cost the client a fetch and still show them nothing.
  const citable =
    Boolean(proposal.has_citation_pdf) &&
    Boolean(proposal.polygon?.length) &&
    typeof proposal.page === "number" &&
    Boolean(proposal.page_width) &&
    Boolean(proposal.page_height);
  const citationPdf = useCitationDocument(proposal.attachment_id, citable);
  const canShowDocument = citable && citationPdf.status === "ready";

  return (
    <div
      className={cn(
        "flex w-full flex-col gap-2.5 rounded-lg p-3 ring-1 transition-colors",
        decision === "accepted"
          ? "bg-success-soft ring-success-line"
          : decision === "rejected"
          ? "bg-fill-1 opacity-60 ring-line-2"
          : "bg-elevated ring-line-2"
      )}
    >
      <div className="flex flex-col gap-1">
        <span className="text-sm font-medium text-fg-3">{proposal.prompt}</span>
        {/* Where this lands, when it is not a field they can see. A value that
            opens a new entry further down the form otherwise reads as nothing
            having happened. */}
        {proposal.repeatable_group_id ? (
          <span className="flex items-center gap-1 text-sm text-fg-3">
            {proposal.creates_group_entry ? (
              <PlusCircle className="size-3" aria-hidden />
            ) : (
              <CornerDownRight className="size-3" aria-hidden />
            )}
            {proposal.creates_group_entry ? "New entry" : "Existing entry"}
            {proposal.repeatable_group_title
              ? ` in ${proposal.repeatable_group_title}`
              : null}
          </span>
        ) : null}
        <span className="text-md leading-snug font-medium wrap-break-word text-fg">
          {isFileProposal ? (
            <span className="inline-flex items-center gap-1.5">
              <Paperclip className="size-3.5 text-fg-3" aria-hidden />
              {proposal.display_value}
            </span>
          ) : (
            proposal.display_value
          )}
        </span>
      </div>

      {proposal.reasoning ? (
        <p className="m-0 text-base leading-snug text-fg-2">
          {proposal.reasoning}
        </p>
      ) : null}

      {proposal.document_filename ? (
        <div className="flex flex-col gap-1.5 rounded-md bg-fill-1 px-2.5 py-2">
          <span className="flex items-center gap-1.5 text-sm font-medium text-fg-2">
            <FileText className="size-3.5 shrink-0" aria-hidden />
            <span className="truncate">{proposal.document_filename}</span>
            {typeof proposal.page === "number" ? (
              <span className="shrink-0 text-fg-4">page {proposal.page}</span>
            ) : null}
          </span>
          {/* The document itself, cropped to the value. Seeing it in place is
              the check a quote only approximates: a client can tell at a glance
              that the line really is the one their answer came from. */}
          {canShowDocument ? (
            <CitationSnippet
              file={citationPdf.url}
              geometry={geometry}
              page={proposal.page as number}
              filename={proposal.document_filename}
              isExpanded={viewer.openCitationId === proposal.proposal_id}
              onExpand={() =>
                viewer.toggle({
                  citationId: proposal.proposal_id,
                  file: citationPdf.url,
                  geometry,
                  page: proposal.page as number,
                  filename: proposal.document_filename
                })
              }
            />
          ) : proposal.reference_text ? (
            <span className="line-clamp-3 text-sm leading-snug text-fg-3 italic">
              &ldquo;{proposal.reference_text}&rdquo;
            </span>
          ) : null}
        </div>
      ) : null}

      {settled ? (
        <span
          className={cn(
            "flex items-center gap-1.5 text-base font-medium",
            decision === "accepted" ? "text-success" : "text-fg-3"
          )}
        >
          {decision === "accepted" ? (
            <Check className="size-3.5" aria-hidden />
          ) : (
            <X className="size-3.5" aria-hidden />
          )}
          {decision === "accepted" ? "Added to your form" : "Dismissed"}
        </span>
      ) : (
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="primary"
            size="md"
            onClick={onAccept}
            disabled={applying}
            className="flex-1 bg-fg text-elevated hover:bg-fg/85"
          >
            {applying ? <Spinner className="size-3.5" /> : <Check />}
            {applying ? "Adding…" : "Use This"}
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="md"
            onClick={onReject}
            disabled={applying}
          >
            No Thanks
          </Button>
        </div>
      )}
    </div>
  );
}
